/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {getDefaultParameters} from '@ion/core/src/utils/QueryStringUtils';

/**
 * Defines the return type for the useEnvQueryParam hook, which is provided
 * as an object with a single `env` property with the value being that which
 * is currently assigned to the `env` parameter in the application query string.
 * If the application query string currently does not contain an `env` parameter,
 * an empty object is returned.
 */
export type UseEnvQueryParamResult = {
  env: string
} | {};

/**
 * Provides a hook which returns the current state of the `env` query parameter.
 * If the application querystring currently contains an `env=` parameter, it will
 * be returned as a serialized object in the form of `{env: <val>}`, for example
 * when the application's query string contains `?env=us`, then this hook will
 * return an object in the form of `{env: 'us'}`. If the application's querystring
 * does not contain a `env` parameter, then an empty object will be returned.
 *
 * ```typescript
 *   import useEnvQueryParam from '@ion/applications/src/hooks/useEnvQueryParam';
 *
 *   // https://app.calamp.com/some-page?env=us
 *   const env = useEnvQueryParam(retainEnvQueryParam); // {env: 'us'} | {}
 *   const qs  = getQueryString({
 *     ...env,
 *     id   : item.id,
 *     date : new Date(item.timestamp)
 *   });
 *   const url = `/some-other-page?${qs}`;
 *   // https://app.calamp.com/some-other-page?env=us&id=<id>&date=<date>
 * ```
 *
 * @param   retain when `true` (default) indicates that the `env` query parameter
 *          is to be returned, otherwise, when `false`, an empty object will be
 *          returned.
 * @returns UseEnvQueryParamResult
 */
export const useEnvQueryParam = (retain = true): UseEnvQueryParamResult => {
  if (retain) {
    const result = getDefaultParameters();
    if (!isObjectEmpty(result)) {
      return result;
    }
  }
  return {};
};
export default useEnvQueryParam;
